import { cn } from '@/lib/cn';
import { Brain, Zap, Minus, Plus, UserCheck, UserX, Gauge } from 'lucide-react';

/**
 * High-frequency one-tap entries for the VitalsTab.
 *
 * The whole point of this module is eliminating clicks on the
 * per-run interactions that fire on EVERY single call. Each widget is
 * a row (not a modal) that accepts a value set with one tap.
 *
 * Addendum design principle P5 ("tap targets not modals") + P8
 * ("short labels, large targets") + benchmark target: AAOx4 in 1 tap,
 * GCS 15 in 1 tap, pain 0-10 in 1 tap, vitals-all-normal in 1 tap.
 *
 * All widgets expose onApply callbacks that the parent uses to
 * merge into the vitals form state.
 */

// ============================================================
// 1. Mental Status quick row — AAOx4 / AAOx3 / AAOx2 / Unresp
// ============================================================
export type MentalStatus = 'aaox4' | 'aaox3' | 'aaox2' | 'aaox1' | 'unresp';

export const MENTAL_STATUS_GCS: Record<MentalStatus, { eye: number; verbal: number; motor: number; label: string; detail: string }> = {
  aaox4:  { eye: 4, verbal: 5, motor: 6, label: 'AAOx4',   detail: 'Alert · oriented person/place/time/event' },
  aaox3:  { eye: 4, verbal: 4, motor: 6, label: 'AAOx3',   detail: 'Alert · one element confused' },
  aaox2:  { eye: 4, verbal: 3, motor: 6, label: 'AAOx2',   detail: 'Confused · two elements uncertain' },
  aaox1:  { eye: 3, verbal: 2, motor: 5, label: 'AAOx1',   detail: 'Responds to voice only · inappropriate words' },
  unresp: { eye: 1, verbal: 1, motor: 1, label: 'Unresp',  detail: 'Unresponsive · GCS 3' },
};

export function MentalStatusQuickRow({
  onApply,
  selected,
}: {
  onApply: (status: MentalStatus) => void;
  selected?: MentalStatus;
}) {
  const opts: Array<{ key: MentalStatus; label: string; tone: 'emerald' | 'blue' | 'amber' | 'orange' | 'red' }> = [
    { key: 'aaox4',  label: 'AAOx4',  tone: 'emerald' },
    { key: 'aaox3',  label: 'AAOx3',  tone: 'blue'    },
    { key: 'aaox2',  label: 'AAOx2',  tone: 'amber'   },
    { key: 'aaox1',  label: 'AAOx1',  tone: 'orange'  },
    { key: 'unresp', label: 'Unresp', tone: 'red'     },
  ];
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Brain size={12} className="text-slate-500" />
        <span className="data-label-dense">Mental Status · 1-tap</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onApply(o.key)}
            className={cn(
              'flex flex-col items-center justify-center py-2 px-1 text-[11px] font-bold uppercase tracking-wider border transition-colors',
              selected === o.key
                ? (o.tone === 'emerald' ? 'bg-emerald-600 border-emerald-400 text-white' :
                   o.tone === 'blue'    ? 'bg-blue-600 border-blue-400 text-white' :
                   o.tone === 'amber'   ? 'bg-amber-600 border-amber-400 text-white' :
                   o.tone === 'orange'  ? 'bg-orange-600 border-orange-400 text-white' :
                   'bg-red-600 border-red-400 text-white')
                : 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700',
            )}
            title={MENTAL_STATUS_GCS[o.key].detail}
          >
            {o.label}
            <span className="text-[9px] font-mono text-current/70 mt-0.5">
              GCS {MENTAL_STATUS_GCS[o.key].eye + MENTAL_STATUS_GCS[o.key].verbal + MENTAL_STATUS_GCS[o.key].motor}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 2. Pain scale 0-10 tap row
// ============================================================
export function PainScaleRow({
  value,
  onApply,
}: {
  value: string;
  onApply: (pain: number) => void;
}) {
  const current = value === '' ? null : Number(value);
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Zap size={12} className="text-slate-500" />
        <span className="data-label-dense">Pain · 0-10 · 1-tap</span>
      </div>
      <div className="grid grid-cols-11 gap-0.5">
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            onClick={() => onApply(n)}
            className={cn(
              'py-1.5 text-sm font-bold tabular-nums border transition-colors',
              current === n
                ? painToneActive(n)
                : 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700',
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-[9px] font-mono uppercase tracking-widest text-slate-600 mt-0.5 px-0.5">
        <span>none</span><span>severe</span>
      </div>
    </div>
  );
}

function painToneActive(n: number): string {
  if (n === 0) return 'bg-emerald-600 border-emerald-400 text-white';
  if (n <= 3)  return 'bg-lime-600 border-lime-400 text-white';
  if (n <= 6)  return 'bg-amber-600 border-amber-400 text-white';
  if (n <= 8)  return 'bg-orange-600 border-orange-400 text-white';
  return 'bg-red-600 border-red-400 text-white';
}

// ============================================================
// 3. GCS quick presets
// ============================================================
interface GcsPreset {
  total: number;
  e: number; v: number; m: number;
  label: string;
  severity: 'mild' | 'moderate' | 'severe';
}

export const GCS_PRESETS: GcsPreset[] = [
  { total: 15, e: 4, v: 5, m: 6, label: 'Fully alert',            severity: 'mild' },
  { total: 13, e: 3, v: 4, m: 6, label: 'Confused/drowsy',        severity: 'mild' },
  { total: 11, e: 3, v: 3, m: 5, label: 'Moderate',               severity: 'moderate' },
  { total:  8, e: 2, v: 2, m: 4, label: 'Severe · airway at risk',severity: 'severe' },
  { total:  3, e: 1, v: 1, m: 1, label: 'Unresponsive',           severity: 'severe' },
];

export function GCSPresetRow({
  onApply,
  currentTotal,
}: {
  onApply: (preset: GcsPreset) => void;
  currentTotal: string;
}) {
  const total = currentTotal === '' ? null : Number(currentTotal);
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Gauge size={12} className="text-slate-500" />
        <span className="data-label-dense">GCS · common presets</span>
        <span className="text-[9px] font-mono text-slate-500">
          (override E/V/M below if needed)
        </span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {GCS_PRESETS.map((p) => (
          <button
            key={p.total}
            type="button"
            onClick={() => onApply(p)}
            className={cn(
              'flex flex-col items-center justify-center py-2 px-1 border text-xs font-bold',
              total === p.total
                ? p.severity === 'mild'     ? 'bg-emerald-600 border-emerald-400 text-white'
                : p.severity === 'moderate' ? 'bg-amber-600 border-amber-400 text-white'
                :                             'bg-red-600 border-red-400 text-white'
                : 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700',
            )}
          >
            <span className="font-mono text-lg tabular-nums">{p.total}</span>
            <span className="text-[9px] font-normal uppercase tracking-wider opacity-80 mt-0.5">{p.label}</span>
            <span className="text-[9px] font-mono text-current/60 mt-0.5">
              E{p.e} V{p.v} M{p.m}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 4. Vitals "Adult Normal" / "Pediatric Normal" quick-fill
// ============================================================
export interface VitalsDefaults {
  bp_systolic: string;
  bp_diastolic: string;
  heart_rate: string;
  respiratory_rate: string;
  spo2: string;
  etco2: string;
  temperature: string;
  blood_glucose: string;
}

export const ADULT_NORMAL: VitalsDefaults = {
  bp_systolic: '120', bp_diastolic: '80',
  heart_rate: '72', respiratory_rate: '16',
  spo2: '98', etco2: '38',
  temperature: '98.6', blood_glucose: '100',
};

export const PEDIATRIC_NORMAL_BY_AGE = (ageYears: number): VitalsDefaults => {
  // Rough midpoints of PALS ranges by age band
  if (ageYears < 1)   return { bp_systolic: '85',  bp_diastolic: '60',  heart_rate: '140', respiratory_rate: '40', spo2: '98', etco2: '38', temperature: '98.6', blood_glucose: '100' };
  if (ageYears < 3)   return { bp_systolic: '95',  bp_diastolic: '65',  heart_rate: '120', respiratory_rate: '30', spo2: '98', etco2: '38', temperature: '98.6', blood_glucose: '100' };
  if (ageYears < 6)   return { bp_systolic: '100', bp_diastolic: '65',  heart_rate: '110', respiratory_rate: '24', spo2: '98', etco2: '38', temperature: '98.6', blood_glucose: '100' };
  if (ageYears < 10)  return { bp_systolic: '105', bp_diastolic: '70',  heart_rate: '95',  respiratory_rate: '20', spo2: '98', etco2: '38', temperature: '98.6', blood_glucose: '100' };
  if (ageYears < 14)  return { bp_systolic: '110', bp_diastolic: '72',  heart_rate: '85',  respiratory_rate: '18', spo2: '98', etco2: '38', temperature: '98.6', blood_glucose: '100' };
  return ADULT_NORMAL;
};

export function VitalsQuickFillBar({
  patientDob,
  onApply,
}: {
  patientDob: string | null;
  onApply: (patch: VitalsDefaults, label: string) => void;
}) {
  const ageYears = patientDob ? ageFromDob(patientDob) : null;
  const isPeds = ageYears !== null && ageYears <= 17;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mr-1">
        Prefill
      </span>
      <button
        type="button"
        onClick={() => onApply(ADULT_NORMAL, 'Adult normal')}
        className="flex items-center gap-1 px-2.5 py-1 border border-emerald-600 bg-emerald-700/40 text-emerald-200 hover:bg-emerald-700/60 text-xs font-bold uppercase tracking-wider"
      >
        <UserCheck size={12} />
        Adult Normal
      </button>
      {isPeds && (
        <button
          type="button"
          onClick={() => onApply(PEDIATRIC_NORMAL_BY_AGE(ageYears!), `Peds normal @ ${Math.floor(ageYears!)}y`)}
          className="flex items-center gap-1 px-2.5 py-1 border border-amber-600 bg-amber-700/40 text-amber-200 hover:bg-amber-700/60 text-xs font-bold uppercase tracking-wider"
        >
          <UserCheck size={12} />
          Peds Normal ({Math.floor(ageYears!)}y)
        </button>
      )}
      <button
        type="button"
        onClick={() => onApply({
          bp_systolic: '', bp_diastolic: '',
          heart_rate: '', respiratory_rate: '',
          spo2: '', etco2: '',
          temperature: '', blood_glucose: '',
        }, 'cleared')}
        className="flex items-center gap-1 px-2.5 py-1 border border-slate-600 bg-slate-800 text-slate-400 hover:bg-slate-700 text-xs font-bold uppercase tracking-wider"
      >
        <UserX size={12} />
        Clear
      </button>
    </div>
  );
}

function ageFromDob(iso: string): number {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
}

// Keep lucide imports alive for future extensions
void Plus; void Minus;
